import React from 'react'
import './ReactionBar.css'

const DEFAULT_REACTION_EMOJIS = ['👍', '❤️', '👏', '😂', '😮', '🙏'] as const

export interface ReactionBarProps {
  emojis?: readonly string[] | string[]
  counts?: Record<string, number>
  mine?: readonly string[] | string[] | Set<string> | string
  onToggle?: (emoji: string) => void
  disabled?: boolean
  readOnly?: boolean
  ariaLabel?: string
  className?: string
  testId?: string
}

export const ReactionBar: React.FC<ReactionBarProps> = ({
  emojis = DEFAULT_REACTION_EMOJIS,
  counts = {},
  mine,
  onToggle,
  disabled = false,
  readOnly = false,
  ariaLabel = 'Reactions',
  className = '',
  testId,
}) => {
  const isMine = (emoji: string): boolean => {
    if (!mine) return false
    if (mine instanceof Set) return mine.has(emoji)
    if (Array.isArray(mine)) return mine.includes(emoji)
    return mine === emoji
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={['reaction-bar', readOnly ? 'reaction-bar--readonly' : '', className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {emojis.map((emoji) => {
        const count = counts[emoji] ?? 0
        const selected = isMine(emoji)
        const chipAriaLabel = `${emoji} ${count}`

        if (readOnly) {
          return (
            <span
              key={emoji}
              className={[
                'reaction-bar__chip',
                'reaction-bar__chip--readonly',
                selected ? 'is-selected' : '',
              ].filter(Boolean).join(' ')}
              aria-label={chipAriaLabel}
            >
              <span className="reaction-bar__emoji" aria-hidden="true">{emoji}</span>
              <span className="reaction-bar__count figures">{count}</span>
            </span>
          )
        }

        return (
          <button
            key={emoji}
            type="button"
            className={[
              'reaction-bar__chip',
              'reaction-bar__chip--interactive',
              selected ? 'is-selected' : '',
            ].filter(Boolean).join(' ')}
            aria-pressed={selected}
            aria-label={chipAriaLabel}
            disabled={disabled}
            onClick={() => {
              if (disabled) return
              onToggle?.(emoji)
            }}
            onKeyDown={(e) => {
              if (disabled) return
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle?.(emoji)
              }
            }}
          >
            <span className="reaction-bar__emoji" aria-hidden="true">{emoji}</span>
            <span className="reaction-bar__count figures">{count}</span>
          </button>
        )
      })}
    </div>
  )
}
